import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Slider,
  Stack,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import {
  BookmarkAdd,
  BookmarkAdded,
  ContentCopy,
  CollectionsBookmark,
  DeleteOutline,
  Edit,
  FilterAlt,
  Explore,
  Favorite,
  FavoriteBorder,
  FmdGood,
  AddLocationAlt,
  Map as MapIcon,
  Public,
  PlaylistAdd,
  Search,
  Share,
  ScubaDiving,
  TravelExplore,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import DiveSiteMap from './DiveSiteMap';
import SignInPromptDialog from './SignInPromptDialog';
import { Dive } from '../types/Dive';
import { CreateCommunityDiveSiteRequest, DiveSite, DiveSiteListVisibility, UpdateCommunityDiveSiteRequest } from '../types/DiveSite';
import { diveService } from '../services/diveService';
import { buildDiveSiteMatches } from '../utils/diveSiteMatcher';
import { useDiveSitePreferences } from '../hooks/useDiveSitePreferences';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { authService, UserLookupResult } from '../services/authService';
import { useNotification } from './NotificationContext';
import { AppOptions, OptionsManager } from '../utils/optionsManager';
import { getDiveSiteRequirementStatus } from '../utils/diveSiteRequirements';

type SiteView = 'all' | 'visited' | 'unvisited' | 'wishlist' | 'favorites';
type DifficultyFilter = 'Beginner' | 'Intermediate' | 'Advanced';
type SiteTypeToggle = 'drift' | 'reef' | 'cave' | 'cavern';

const MAX_DEPTH_CEILING = 60;
type CollectionFilter = 'all' | 'caribbean' | 'florida' | 'south-florida' | 'florida-keys' | 'florida-springs' | 'florida-west-coast' | 'florida-fossils-training' | 'global-featured';

const collectionOptions: Array<{ value: CollectionFilter; label: string }> = [
  { value: 'all', label: 'All catalog' },
  { value: 'caribbean', label: 'Caribbean' },
  { value: 'florida', label: 'Florida' },
  { value: 'south-florida', label: 'South Florida + Palm Beach' },
  { value: 'florida-keys', label: 'Florida Keys' },
  { value: 'florida-springs', label: 'Springs + caverns' },
  { value: 'florida-west-coast', label: 'West Coast + Gulf' },
  { value: 'florida-fossils-training', label: 'Fossils + training' },
  { value: 'global-featured', label: 'Global featured' },
];

const emptyFormValues = {
  name: '',
  region: '',
  country: '',
  latitude: '',
  longitude: '',
  matchRadiusKm: '25',
  siteType: 'destination',
  difficulty: 'Intermediate',
  summary: '',
  highlights: '',
  tags: '',
  aliases: '',
};

const siteTypeOptions: Array<{ value: DiveSite['siteType']; label: string }> = [
  { value: 'destination', label: 'Destination' },
  { value: 'reef', label: 'Reef' },
  { value: 'wreck', label: 'Wreck' },
  { value: 'blue-hole', label: 'Blue hole' },
  { value: 'cold-water', label: 'Cold water' },
  { value: 'drift', label: 'Drift' },
  { value: 'lake', label: 'Lake' },
  { value: 'quarry', label: 'Quarry' },
  { value: 'river', label: 'River' },
  { value: 'spring', label: 'Spring' },
];

const formatVisitDate = (date: string): string => {
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatListVisibility = (visibility: DiveSiteListVisibility): string => {
  if (visibility === 'invite-only') {
    return 'Invite only';
  }

  if (visibility === 'link') {
    return 'Shared by link';
  }

  return 'Private';
};

const matchesSearch = (site: DiveSite, searchTerm: string): boolean => {
  if (!searchTerm) {
    return true;
  }

  const haystack = [
    site.name,
    site.region,
    site.country,
    site.summary,
    site.siteType,
    site.difficulty,
    ...site.tags,
    ...site.highlights,
    ...(site.aliases ?? []),
  ].join(' ').toLowerCase();

  return haystack.includes(searchTerm);
};

const matchesCollection = (site: DiveSite, collection: CollectionFilter): boolean => {
  if (collection === 'all') {
    return true;
  }

  if (collection === 'global-featured') {
    return !site.tags.includes('florida') && Boolean(site.featured);
  }

  if (collection === 'caribbean') {
    return site.tags.includes('caribbean');
  }

  if (collection === 'florida') {
    return site.tags.includes('florida');
  }

  if (collection === 'south-florida') {
    return site.tags.includes('south florida') || site.tags.includes('palm beach');
  }

  if (collection === 'florida-keys') {
    return site.tags.includes('keys');
  }

  if (collection === 'florida-springs') {
    return site.tags.includes('spring') || site.tags.includes('cavern') || site.tags.includes('cave');
  }

  if (collection === 'florida-west-coast') {
    return site.tags.includes('west coast florida') || site.tags.includes('gulf') || site.tags.includes('tampa bay');
  }

  if (collection === 'florida-fossils-training') {
    return site.tags.includes('fossil') || site.tags.includes('training');
  }

  return true;
};

const CAVE_CERTS = new Set(['IntroCave', 'FullCave', 'Tec40', 'Tec45', 'Tec50', 'TecTrimix']);
const CAVERN_CERTS = new Set(['Cavern', 'IntroCave', 'FullCave']);

const matchesSiteTypeToggle = (site: DiveSite, toggles: Set<SiteTypeToggle>): boolean => {
  if (toggles.size === 0) {
    return true;
  }

  for (const toggle of Array.from(toggles)) {
    if (toggle === 'drift' && (site.siteType === 'drift' || site.tags.includes('drift'))) {
      return true;
    }
    if (toggle === 'reef' && (site.siteType === 'reef' || site.tags.includes('reef'))) {
      return true;
    }
    if (toggle === 'cave' && site.requiredCertification && CAVE_CERTS.has(site.requiredCertification)) {
      return true;
    }
    if (toggle === 'cavern' && (
      (site.requiredCertification && CAVERN_CERTS.has(site.requiredCertification)) ||
      site.tags.includes('cavern') ||
      site.tags.includes('cave')
    )) {
      return true;
    }
  }

  return false;
};

const DiveSites: React.FC = () => {
  const navigate = useNavigate();
  const optionsManager = OptionsManager.getInstance();
  const [signInPrompt, setSignInPrompt] = useState<string | null>(null);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isCreateListDialogOpen, setIsCreateListDialogOpen] = useState(false);
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false);
  const [listDialogSite, setListDialogSite] = useState<DiveSite | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [editingSite, setEditingSite] = useState<DiveSite | null>(null);
  const [formValues, setFormValues] = useState(emptyFormValues);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [newListVisibility, setNewListVisibility] = useState<DiveSiteListVisibility>('private');
  const [collaboratorUserIdsInput, setCollaboratorUserIdsInput] = useState('');
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const [collaboratorSearchResults, setCollaboratorSearchResults] = useState<UserLookupResult[]>([]);
  const [collaboratorSearchLoading, setCollaboratorSearchLoading] = useState(false);
  const [resolvedCollaborators, setResolvedCollaborators] = useState<UserLookupResult[]>([]);
  const [dives, setDives] = useState<Dive[]>([]);
  const { showSuccess, showError, showInfo } = useNotification();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<SiteView>('all');
  const [collection, setCollection] = useState<CollectionFilter>('all');
  const [difficultyFilters, setDifficultyFilters] = useState<Set<DifficultyFilter>>(new Set());
  const [siteTypeToggles, setSiteTypeToggles] = useState<Set<SiteTypeToggle>>(new Set());
  const [maxDepthFilter, setMaxDepthFilter] = useState<number>(MAX_DEPTH_CEILING);
  const [diverOptions, setDiverOptions] = useState<AppOptions['diver']>(() => optionsManager.getOptions().diver);
  const [selectedCustomListId, setSelectedCustomListId] = useState<string | null>(null);
  const {
    allSites,
    communitySites,
    loading: catalogLoading,
    saving: catalogSaving,
    error: catalogError,
    addCommunitySite,
    updateCommunitySite,
  } = useDiveSiteCatalog();
  const {
    preferences: sitePreferences,
    customLists,
    loading: preferencesLoading,
    saving: preferencesSaving,
    error: preferencesError,
    isFavorite,
    isWishlisted,
    isSiteInCustomList,
    getCustomListsForSite,
    toggleFavorite,
    toggleWishlist,
    createCustomList,
    deleteCustomList,
    toggleSiteInCustomList,
    updateCustomList,
  } = useDiveSitePreferences();

  const parseList = (value: string) => value.split(',').map((entry) => entry.trim()).filter(Boolean);
  const slugify = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const currentUser = authService.getUser();
  const userId = currentUser?.userId;

  useEffect(() => {
    const unsubscribe = optionsManager.subscribe((options) => {
      setDiverOptions(options.diver);
    });

    return unsubscribe;
  }, [optionsManager]);

  const resetForm = () => {
    setEditingSite(null);
    setFormValues(emptyFormValues);
  };

  const resetListForm = () => {
    setNewListName('');
    setNewListDescription('');
    setNewListVisibility('private');
    setCollaboratorUserIdsInput('');
    setCollaboratorSearch('');
    setCollaboratorSearchResults([]);
    setCollaboratorSearchLoading(false);
    setResolvedCollaborators([]);
    setListError(null);
  };

  const openAccessDialog = () => {
    if (!selectedCustomList) {
      return;
    }

    setListError(null);
    setNewListName(selectedCustomList.name);
    setNewListDescription(selectedCustomList.description || '');
    setNewListVisibility(selectedCustomList.visibility);
    setCollaboratorUserIdsInput(selectedCustomList.collaboratorUserIds.join('\n'));
    setIsAccessDialogOpen(true);
  };

  const populateForm = (site: DiveSite) => {
    setEditingSite(site);
    setFormValues({
      name: site.name,
      region: site.region,
      country: site.country,
      latitude: site.latitude.toString(),
      longitude: site.longitude.toString(),
      matchRadiusKm: site.matchRadiusKm.toString(),
      siteType: site.siteType,
      difficulty: site.difficulty,
      summary: site.summary,
      highlights: site.highlights.join(', '),
      tags: site.tags.join(', '),
      aliases: (site.aliases ?? []).join(', '),
    });
  };

  const handleSubmitCommunitySite = async () => {
    try {
      setSubmitError(null);
      setSubmitSuccess(null);

      const slug = slugify(formValues.name);
      if (slug && allSites.some((site) => site.slug === slug && site.id !== editingSite?.id)) {
        setSubmitError('A dive site with that name already exists in the catalog.');
        return;
      }

      const payload: CreateCommunityDiveSiteRequest = {
        name: formValues.name.trim(),
        region: formValues.region.trim(),
        country: formValues.country.trim(),
        latitude: Number(formValues.latitude),
        longitude: Number(formValues.longitude),
        matchRadiusKm: Number(formValues.matchRadiusKm),
        siteType: formValues.siteType as DiveSite['siteType'],
        difficulty: formValues.difficulty as DiveSite['difficulty'],
        summary: formValues.summary.trim(),
        highlights: parseList(formValues.highlights),
        tags: parseList(formValues.tags),
        aliases: parseList(formValues.aliases),
      };

      const site = editingSite
        ? await updateCommunitySite({
            ...(payload as UpdateCommunityDiveSiteRequest),
            siteId: editingSite.siteId || editingSite.id,
          })
        : await addCommunitySite(payload);

      setSubmitSuccess(
        editingSite
          ? `${site.name} has been updated in the shared dive-site catalog.`
          : `${site.name} is now part of the shared dive-site catalog.`
      );
      setIsSubmitDialogOpen(false);
      resetForm();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add dive site';
      setSubmitError(message);
    }
  };

  const handleCreateCustomList = async () => {
    try {
      setListError(null);
      const nextList = await createCustomList(newListName, newListDescription, newListVisibility);
      setSelectedCustomListId(nextList.listId);
      setIsCreateListDialogOpen(false);
      resetListForm();
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to create dive-site list');
    }
  };

  const handleDeleteCustomList = async (listId: string) => {
    const currentList = customLists.find((list) => list.listId === listId);
    if (!currentList) {
      return;
    }

    if (!window.confirm(`Delete the list "${currentList.name}"?`)) {
      return;
    }

    try {
      setListError(null);
      await deleteCustomList(listId);
      if (selectedCustomListId === listId) {
        setSelectedCustomListId(null);
      }
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to delete dive-site list');
    }
  };

  const handleSaveListAccess = async () => {
    if (!selectedCustomList) {
      return;
    }

    const collaboratorUserIds = collaboratorUserIdsInput
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean);

    try {
      setListError(null);
      const updatedList = await updateCustomList(selectedCustomList.listId, {
        name: newListName,
        description: newListDescription,
        visibility: newListVisibility,
        collaboratorUserIds,
      });
      setSelectedCustomListId(updatedList.listId);
      setIsAccessDialogOpen(false);
      resetListForm();
      showSuccess('Dive-site list access updated.');
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to update list access');
    }
  };

  const handleCopyShareLink = async () => {
    await copyShareLink(selectedCustomList ?? undefined);
  };

  const copyShareLink = async (list?: { shareCode?: string; visibility?: DiveSiteListVisibility }) => {
    if (!list?.shareCode || list.visibility !== 'link') {
      showInfo('Switch this list to shared by link before copying a public URL.');
      return;
    }

    const shareUrl = `${window.location.origin}/dive-site-lists/shared/${list.shareCode}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        throw new Error('Clipboard API unavailable');
      }
      showSuccess('Shared list link copied to clipboard.');
    } catch (err) {
      console.error('Failed to copy shared list URL:', err);
      showError(`Copy failed. Share this URL manually: ${shareUrl}`);
    }
  };

  const openSharedList = (shareCode?: string) => {
    if (!shareCode) {
      showInfo('Switch this list to shared by link before opening a public preview.');
      return;
    }

    navigate(`/dive-site-lists/shared/${shareCode}`);
  };

  const copyAccessLink = async (list?: { listId?: string }) => {
    if (!list?.listId) {
      showInfo('Choose a list first.');
      return;
    }

    const accessUrl = `${window.location.origin}/dive-site-lists/${list.listId}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(accessUrl);
      } else {
        throw new Error('Clipboard API unavailable');
      }
      showSuccess('Collaborator access link copied to clipboard.');
    } catch (err) {
      console.error('Failed to copy secure list URL:', err);
      showError(`Copy failed. Share this URL manually: ${accessUrl}`);
    }
  };

  const openAuthenticatedList = (listId?: string) => {
    if (!listId) {
      showInfo('Choose a list first.');
      return;
    }

    navigate(`/dive-site-lists/${listId}`);
  };

  useEffect(() => {
    // Guests never had dives to load — skip the call rather than let it 401 and
    // get misread as an expired session.
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return;
    }

    const loadDives = async () => {
      try {
        setLoading(true);
        setError(null);
        const diveData = await diveService.getMyDives();
        setDives(diveData);
      } catch (err: any) {
        console.error('Failed to load dives for dive sites page:', err);
        if (err.message?.includes('Unauthorized')) {
          setError('Your session has expired. Browse the catalog below, or log in again to see sites matched to your dives.');
        } else {
          setError('Unable to load your dives right now. You can still browse the site catalog.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadDives();
  }, []);

  const collaboratorUserIds = collaboratorUserIdsInput
    .split(/[\n,]/)
    .map((value) => value.trim())
    .filter((value, index, values) => Boolean(value) && values.indexOf(value) === index);

  useEffect(() => {
    if (!isAccessDialogOpen || !authService.isAuthenticated()) {
      setCollaboratorSearchResults([]);
      setCollaboratorSearchLoading(false);
      return;
    }

    const normalizedQuery = collaboratorSearch.trim();
    if (normalizedQuery.length < 2) {
      setCollaboratorSearchResults([]);
      setCollaboratorSearchLoading(false);
      return;
    }

    let isCancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setCollaboratorSearchLoading(true);
      const results = await authService.searchUsers(normalizedQuery);
      if (!isCancelled) {
        setCollaboratorSearchResults(results);
        setCollaboratorSearchLoading(false);
      }
    }, 250);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [collaboratorSearch, isAccessDialogOpen]);

  useEffect(() => {
    if (!isAccessDialogOpen || !authService.isAuthenticated() || collaboratorUserIds.length === 0) {
      setResolvedCollaborators([]);
      return;
    }

    let isCancelled = false;

    const loadCollaborators = async () => {
      const users = await authService.getUsersByIds(collaboratorUserIds);
      if (!isCancelled) {
        setResolvedCollaborators(users);
      }
    };

    loadCollaborators();

    return () => {
      isCancelled = true;
    };
  }, [collaboratorUserIds, isAccessDialogOpen]);

  const siteMatches = buildDiveSiteMatches(dives, allSites, sitePreferences.ignoredDiveSiteMatches);
  const normalizedSearch = search.trim().toLowerCase();
  const doveCount = Array.from(siteMatches.keys()).length;
  const matchedDiveCount = Array.from(siteMatches.values()).reduce((total, match) => total + match.diveCount, 0);
  const favoriteCount = sitePreferences.favoriteSiteIds.length;
  const wishlistCount = sitePreferences.wishlistSiteIds.length;
  const selectedCustomList = customLists.find((list) => list.listId === selectedCustomListId) ?? null;
  const regionOptions = Array.from(new Set(allSites.filter((site) => matchesCollection(site, collection)).map((site) => site.region))).sort((left, right) => left.localeCompare(right));
  const caribbeanCount = allSites.filter((site) => site.tags.includes('caribbean')).length;
  const floridaSiteCount = allSites.filter((site) => site.tags.includes('florida')).length;
  const southFloridaCount = allSites.filter((site) => site.tags.includes('south florida') || site.tags.includes('palm beach')).length;
  const westCoastCount = allSites.filter((site) => site.tags.includes('west coast florida') || site.tags.includes('gulf') || site.tags.includes('tampa bay')).length;
  const specialtyCount = allSites.filter((site) => site.tags.includes('fossil') || site.tags.includes('training')).length;
  const floridaDoveCount = allSites.filter((site) => site.tags.includes('florida') && siteMatches.has(site.id)).length;
  const collaboratorLookup = new Map(resolvedCollaborators.map((user) => [user.userId, user]));

  const formatCollaboratorLabel = (collaboratorUserId: string): string => {
    if (collaboratorUserId === userId) {
      return 'You';
    }

    const collaborator = collaboratorLookup.get(collaboratorUserId);
    if (!collaborator) {
      return collaboratorUserId;
    }

    const fullName = `${collaborator.firstName} ${collaborator.lastName}`.trim();
    return fullName || collaborator.email || collaboratorUserId;
  };

  const syncCollaboratorUserIds = (nextUserIds: string[]) => {
    setCollaboratorUserIdsInput(nextUserIds.join('\n'));
  };

  const addCollaboratorUserId = (nextUserId: string) => {
    if (collaboratorUserIds.includes(nextUserId)) {
      return;
    }

    syncCollaboratorUserIds([...collaboratorUserIds, nextUserId]);
  };

  const removeCollaboratorUserId = (userIdToRemove: string) => {
    syncCollaboratorUserIds(collaboratorUserIds.filter((value) => value !== userIdToRemove));
  };

  const visibleSites = allSites
    .filter((site) => matchesCollection(site, collection))
    .filter((site) => matchesSearch(site, normalizedSearch))
    .filter((site) => {
      if (!selectedCustomListId) {
        return true;
      }

      return selectedCustomList?.siteIds.includes(site.id) ?? false;
    })
    .filter((site) => {
      const hasVisit = siteMatches.has(site.id);
      if (view === 'visited') {
        return hasVisit;
      }
      if (view === 'favorites') {
        return isFavorite(site.id);
      }
      if (view === 'wishlist') {
        return isWishlisted(site.id);
      }
      if (view === 'unvisited') {
        return !hasVisit;
      }
      return true;
    })
    .filter((site) => difficultyFilters.size === 0 || difficultyFilters.has(site.difficulty as DifficultyFilter))
    .filter((site) => maxDepthFilter < MAX_DEPTH_CEILING ? (site.maxDepthM == null || site.maxDepthM <= maxDepthFilter) : true)
    .filter((site) => matchesSiteTypeToggle(site, siteTypeToggles))
    .sort((left, right) => {
      const leftMatch = siteMatches.get(left.id);
      const rightMatch = siteMatches.get(right.id);
      const leftFavorite = isFavorite(left.id);
      const rightFavorite = isFavorite(right.id);

      if (leftFavorite && !rightFavorite) {
        return -1;
      }
      if (!leftFavorite && rightFavorite) {
        return 1;
      }

      if (leftMatch && !rightMatch) {
        return -1;
      }
      if (!leftMatch && rightMatch) {
        return 1;
      }
      if (leftMatch && rightMatch && leftMatch.diveCount !== rightMatch.diveCount) {
        return rightMatch.diveCount - leftMatch.diveCount;
      }
      if (left.featured && !right.featured) {
        return -1;
      }
      if (!left.featured && right.featured) {
        return 1;
      }
      return left.name.localeCompare(right.name);
    });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexDirection: { xs: 'column', md: 'row' }, mb: 3 }}>
        <Box>
          <Typography variant="h4" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
            World Dive Sites
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
            Browse a curated set of standout dive destinations, then compare them against your logged GPS data to see where you have already dove.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Chip
            icon={loading ? <CircularProgress size={16} color="inherit" /> : <TravelExplore />}
            label={loading ? 'Checking your logged dives...' : `${doveCount} matched dive destinations`}
            color={doveCount > 0 ? 'success' : 'default'}
            variant={doveCount > 0 ? 'filled' : 'outlined'}
          />
          <Button
            variant="contained"
            startIcon={<AddLocationAlt />}
            onClick={() => {
              setSubmitError(null);
              setSubmitSuccess(null);
              resetForm();
              setIsSubmitDialogOpen(true);
            }}
            disabled={!authService.isAuthenticated()}
          >
            Add dive site
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {preferencesError && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {preferencesError}
        </Alert>
      )}

      {catalogError && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {catalogError}
        </Alert>
      )}

      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {submitSuccess}
        </Alert>
      )}

      {!authService.isAuthenticated() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Log in to add a dive site to the shared catalog for everyone.
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterAlt color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Collections
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {floridaDoveCount} Florida destinations matched from your current dive history.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
          {collectionOptions.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              color={collection === option.value ? 'primary' : 'default'}
              variant={collection === option.value ? 'filled' : 'outlined'}
              onClick={() => setCollection(option.value)}
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip size="small" variant="outlined" label={`${caribbeanCount} Caribbean sites`} />
          <Chip size="small" variant="outlined" label={`${floridaSiteCount} Florida sites`} />
          <Chip size="small" variant="outlined" label={`${southFloridaCount} South Florida + Palm Beach`} />
          <Chip size="small" variant="outlined" label={`${westCoastCount} West Coast + Gulf`} />
          <Chip size="small" variant="outlined" label={`${specialtyCount} Fossil/training sites`} />
          <Chip size="small" variant="outlined" label={`${regionOptions.length} regions in current collection`} />
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Public color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Catalog Size
              </Typography>
            </Stack>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
              {allSites.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {communitySites.length} community additions layered on top of the curated site catalog.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <FmdGood color="success" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Sites Dove
              </Typography>
            </Stack>
            <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
              {doveCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Destinations matched from your logged dive coordinates first, then name-based fallback when GPS is missing.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <ScubaDiving color="info" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Logged Site Dives
              </Typography>
            </Stack>
            <Typography variant="h4" color="info.main" sx={{ fontWeight: 700 }}>
              {matchedDiveCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dives that currently fall into the combined curated and community catalog.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Favorite color="warning" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Saved Lists
              </Typography>
            </Stack>
            <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
              {favoriteCount + wishlistCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {favoriteCount} favorites and {wishlistCount} want-to-dive picks synced to your profile when available.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterAlt color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Filter Catalog
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                variant="outlined"
                label={`${visibleSites.length} visible`}
              />
              {(search || view !== 'all' || difficultyFilters.size > 0 || siteTypeToggles.size > 0 || maxDepthFilter < MAX_DEPTH_CEILING) && (
                <Button
                  size="small"
                  onClick={() => {
                    setSearch('');
                    setView('all');
                    setSelectedCustomListId(null);
                    setDifficultyFilters(new Set());
                    setSiteTypeToggles(new Set());
                    setMaxDepthFilter(MAX_DEPTH_CEILING);
                  }}
                >
                  Reset filters
                </Button>
              )}
            </Stack>
          </Stack>

          <TextField
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by site, country, region, Florida area, or marine-life tag"
            InputProps={{
              startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
          />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Show
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                label="All"
                color={view === 'all' ? 'primary' : 'default'}
                variant={view === 'all' ? 'filled' : 'outlined'}
                onClick={() => setView('all')}
              />
              <Chip
                icon={<FmdGood />}
                label="Dove"
                color={view === 'visited' ? 'success' : 'default'}
                variant={view === 'visited' ? 'filled' : 'outlined'}
                onClick={() => setView('visited')}
              />
              <Chip
                icon={<Favorite />}
                label="Favorites"
                color={view === 'favorites' ? 'warning' : 'default'}
                variant={view === 'favorites' ? 'filled' : 'outlined'}
                onClick={() => setView('favorites')}
              />
              <Chip
                icon={<BookmarkAdded />}
                label="Want to dive"
                color={view === 'wishlist' ? 'primary' : 'default'}
                variant={view === 'wishlist' ? 'filled' : 'outlined'}
                onClick={() => setView('wishlist')}
              />
              <Chip
                icon={<Explore />}
                label="To Explore"
                color={view === 'unvisited' ? 'info' : 'default'}
                variant={view === 'unvisited' ? 'filled' : 'outlined'}
                onClick={() => setView('unvisited')}
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Difficulty
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {(['Beginner', 'Intermediate', 'Advanced'] as DifficultyFilter[]).map((level) => (
                <Chip
                  key={level}
                  label={level}
                  color={difficultyFilters.has(level) ? 'primary' : 'default'}
                  variant={difficultyFilters.has(level) ? 'filled' : 'outlined'}
                  onClick={() => setDifficultyFilters((prev) => {
                    const next = new Set(prev);
                    if (next.has(level)) {
                      next.delete(level);
                    } else {
                      next.add(level);
                    }
                    return next;
                  })}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Site type
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {([
                { value: 'reef', label: 'Reef' },
                { value: 'drift', label: 'Drift' },
                { value: 'cavern', label: 'Cavern' },
                { value: 'cave', label: 'Cave (cert required)' },
              ] as Array<{ value: SiteTypeToggle; label: string }>).map(({ value, label }) => (
                <Chip
                  key={value}
                  label={label}
                  color={siteTypeToggles.has(value) ? 'secondary' : 'default'}
                  variant={siteTypeToggles.has(value) ? 'filled' : 'outlined'}
                  onClick={() => setSiteTypeToggles((prev) => {
                    const next = new Set(prev);
                    if (next.has(value)) {
                      next.delete(value);
                    } else {
                      next.add(value);
                    }
                    return next;
                  })}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Max site depth{maxDepthFilter < MAX_DEPTH_CEILING ? `: ≤ ${maxDepthFilter} m` : ' (any)'}
            </Typography>
            <Box sx={{ px: 1 }}>
              <Slider
                value={maxDepthFilter}
                min={10}
                max={MAX_DEPTH_CEILING}
                step={5}
                marks={[
                  { value: 10, label: '10 m' },
                  { value: 20, label: '20 m' },
                  { value: 30, label: '30 m' },
                  { value: 40, label: '40 m' },
                  { value: 60, label: 'Any' },
                ]}
                onChange={(_event, value) => setMaxDepthFilter(value as number)}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => value >= MAX_DEPTH_CEILING ? 'Any' : `${value} m`}
                sx={{ maxWidth: 480 }}
              />
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <CollectionsBookmark color="primary" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Specialized Lists
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Build named trip, training, species, or region lists on top of favorites and wishlist.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<PlaylistAdd />}
              onClick={() => {
                if (!authService.isAuthenticated()) { setSignInPrompt('Creating dive site lists'); return; }
                setListError(null);
                setIsCreateListDialogOpen(true);
              }}
            >
              New list
            </Button>
          </Stack>

          {listError ? <Alert severity="error">{listError}</Alert> : null}

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              label="All sites"
              color={selectedCustomListId === null ? 'primary' : 'default'}
              variant={selectedCustomListId === null ? 'filled' : 'outlined'}
              onClick={() => setSelectedCustomListId(null)}
            />
            {customLists.map((list) => (
              <Chip
                key={list.listId}
                label={`${list.name} (${list.siteIds.length})`}
                color={selectedCustomListId === list.listId ? 'primary' : 'default'}
                variant={selectedCustomListId === list.listId ? 'filled' : 'outlined'}
                onClick={() => setSelectedCustomListId(list.listId)}
              />
            ))}
            {customLists.length === 0 ? <Chip variant="outlined" label="No custom lists yet" /> : null}
          </Stack>

          {customLists.length > 0 ? (
            <Stack spacing={1}>
              {customLists.map((list) => (
                <Paper key={`${list.listId}-actions`} variant="outlined" sx={{ p: 1.25 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {list.name}
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                        <Chip size="small" variant="outlined" label={`${list.siteIds.length} sites`} />
                        <Chip size="small" variant="outlined" icon={<Public fontSize="small" />} label={formatListVisibility(list.visibility)} />
                        {list.shareCode ? <Chip size="small" variant="outlined" label={`Code ${list.shareCode}`} /> : null}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Button size="small" onClick={() => setSelectedCustomListId(list.listId)}>
                        Focus
                      </Button>
                        {list.visibility === 'link' ? (
                          <Button size="small" startIcon={<Share />} onClick={() => copyShareLink(list)} disabled={!list.shareCode}>
                            Copy link
                          </Button>
                        ) : (
                          <Button size="small" startIcon={<ContentCopy />} onClick={() => copyAccessLink(list)}>
                            Copy access
                          </Button>
                        )}
                        {list.visibility === 'link' ? (
                          <Button size="small" startIcon={<Public />} onClick={() => openSharedList(list.shareCode)} disabled={!list.shareCode}>
                            Open shared
                          </Button>
                        ) : (
                          <Button size="small" startIcon={<Public />} onClick={() => openAuthenticatedList(list.listId)}>
                            Open secure
                          </Button>
                        )}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : null}

          {selectedCustomList ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {selectedCustomList.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCustomList.description || 'No description yet.'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Chip size="small" variant="outlined" label={`${selectedCustomList.siteIds.length} sites`} />
                  <Chip size="small" variant="outlined" icon={<Public fontSize="small" />} label={formatListVisibility(selectedCustomList.visibility)} />
                  {selectedCustomList.shareCode ? <Chip size="small" variant="outlined" label={`Code ${selectedCustomList.shareCode}`} /> : null}
                  {selectedCustomList.collaboratorUserIds.length > 0 ? <Chip size="small" variant="outlined" label={`${selectedCustomList.collaboratorUserIds.length} collaborators`} /> : null}
                  {userId && selectedCustomList.visibility === 'link' ? (
                    <Button size="small" startIcon={<Share />} onClick={handleCopyShareLink}>
                      Copy link
                    </Button>
                  ) : null}
                  {userId && selectedCustomList.visibility !== 'link' ? (
                    <Button size="small" startIcon={<ContentCopy />} onClick={() => copyAccessLink(selectedCustomList)}>
                      Copy access
                    </Button>
                  ) : null}
                  {userId && selectedCustomList.visibility === 'link' ? (
                    <Button size="small" startIcon={<Public />} onClick={() => openSharedList(selectedCustomList.shareCode)}>
                      Open shared
                    </Button>
                  ) : null}
                  {userId && selectedCustomList.visibility !== 'link' ? (
                    <Button size="small" startIcon={<Public />} onClick={() => openAuthenticatedList(selectedCustomList.listId)}>
                      Open secure
                    </Button>
                  ) : null}
                  {userId ? (
                    <Button size="small" startIcon={<Edit />} onClick={openAccessDialog}>
                      Access
                    </Button>
                  ) : null}
                  <Button size="small" color="error" startIcon={<DeleteOutline />} onClick={() => handleDeleteCustomList(selectedCustomList.listId)}>
                    Delete list
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <MapIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Catalog map
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip size="small" color="success" label="Dove" />
            <Chip size="small" color="warning" label="Favorite" />
            <Chip size="small" color="primary" variant="outlined" label="Want to dive" />
            <Chip
              size="small"
              variant="outlined"
              label={preferencesLoading || preferencesSaving ? 'Syncing lists...' : `${visibleSites.length} sites on map`}
            />
          </Stack>
        </Stack>
        <DiveSiteMap sites={visibleSites} siteMatches={siteMatches} preferences={sitePreferences} height={380} dialogTitle="Dive site catalog map" />
      </Paper>

      {catalogLoading && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading curated and community dive sites...
          </Typography>
        </Stack>
      )}

      {visibleSites.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No dive sites matched the current search and filter combination.
        </Alert>
      )}

      <Grid container spacing={2}>
        {visibleSites.map((site) => {
          const match = siteMatches.get(site.id);
          const favorite = isFavorite(site.id);
          const wishlisted = isWishlisted(site.id);
          const siteLists = getCustomListsForSite(site.id);
          const canEdit = site.source === 'community' && Boolean(userId) && site.submittedByUserId === userId;
          const requirementStatus = getDiveSiteRequirementStatus(site, diverOptions);
          const shouldHighlightRequirement = requirementStatus.chips.length > 0 && !requirementStatus.meetsRequirements;

          return (
            <Grid item xs={12} md={6} lg={4} key={site.id}>
              <Card
                sx={(theme) => ({
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderWidth: shouldHighlightRequirement ? 2 : 1,
                  borderStyle: 'solid',
                  borderColor: shouldHighlightRequirement ? theme.palette.warning.main : theme.palette.divider,
                  backgroundColor: shouldHighlightRequirement ? alpha(theme.palette.warning.main, 0.08) : theme.palette.background.paper,
                  boxShadow: shouldHighlightRequirement ? theme.shadows[4] : theme.shadows[1],
                })}
              >
                <CardActionArea
                  onClick={() => navigate(`/dive-sites/${site.slug}`)}
                  sx={{ alignItems: 'stretch', display: 'flex', flexDirection: 'column', flexGrow: 1 }}
                >
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%', width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {site.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {site.region}, {site.country}
                        </Typography>
                      </Box>
                      {match ? (
                        <Chip color="success" label={`Dove ${match.diveCount}x`} />
                      ) : site.featured ? (
                        <Chip color="primary" variant="outlined" label="Featured" />
                      ) : null}
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      {site.summary}
                    </Typography>

                    {shouldHighlightRequirement ? (
                      <Paper
                        variant="outlined"
                        sx={(theme) => ({
                          p: 1.25,
                          borderColor: theme.palette.warning.main,
                          backgroundColor: alpha(theme.palette.warning.main, 0.08),
                        })}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Certification Check
                        </Typography>
                        {requirementStatus.warningMessage ? (
                          <Typography variant="body2" color="text.secondary">
                            {requirementStatus.warningMessage}
                          </Typography>
                        ) : null}
                        {requirementStatus.note ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {requirementStatus.note}
                          </Typography>
                        ) : null}
                      </Paper>
                    ) : null}

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip size="small" icon={<Explore />} label={site.siteType.replace('-', ' ')} />
                      <Chip size="small" label={site.difficulty} />
                      {requirementStatus.chips.map((label) => (
                        <Chip
                          key={label}
                          size="small"
                          color={requirementStatus.meetsRequirements ? 'info' : 'warning'}
                          variant={requirementStatus.meetsRequirements ? 'outlined' : 'filled'}
                          label={label}
                        />
                      ))}
                      {site.source === 'community' ? <Chip size="small" color="info" variant="outlined" label="Community" /> : null}
                      {favorite ? <Chip size="small" color="warning" label="Favorite" /> : null}
                      {wishlisted ? <Chip size="small" color="primary" variant="outlined" label="Want to dive" /> : null}
                      {siteLists.slice(0, 2).map((list) => (
                        <Chip key={list.listId} size="small" color="secondary" variant="outlined" label={list.name} />
                      ))}
                      {site.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} size="small" variant="outlined" label={tag} />
                      ))}
                    </Stack>

                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Highlights
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {site.highlights.join(' • ')}
                      </Typography>
                      {site.source === 'community' && site.submittedByName ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          Added by {site.submittedByName}
                        </Typography>
                      ) : null}
                    </Box>

                    <Divider sx={{ my: 0.5 }} />

                    {match ? (
                      <Paper variant="outlined" sx={{ p: 1.5, mt: 'auto' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Your History Here
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last dive {formatVisitDate(match.lastVisited)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Match source: {match.matchType === 'coordinates' ? 'GPS proximity' : 'location text'}
                        </Typography>
                        {match.nearestDistanceKm != null && (
                          <Typography variant="body2" color="text.secondary">
                            Nearest logged dive: {match.nearestDistanceKm.toFixed(1)} km from the catalog point
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                          {match.matchedDives.slice(0, 3).map((dive) => (
                            <Chip
                              key={dive.id}
                              size="small"
                              color="success"
                              variant="outlined"
                              label={dive.diveName || formatVisitDate(dive.startTime)}
                            />
                          ))}
                        </Stack>
                      </Paper>
                    ) : (
                      <Paper variant="outlined" sx={{ p: 1.5, mt: 'auto' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Not Logged Yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          This destination is in the catalog but has not been matched to any of your current dives.
                        </Typography>
                      </Paper>
                    )}
                  </CardContent>
                </CardActionArea>
                <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" onClick={() => navigate(`/dive-sites/${site.slug}`)}>
                    View details
                  </Button>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                    {canEdit ? (
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSubmitError(null);
                          setSubmitSuccess(null);
                          populateForm(site);
                          setIsSubmitDialogOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      color={favorite ? 'warning' : 'inherit'}
                      startIcon={favorite ? <Favorite /> : <FavoriteBorder />}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!authService.isAuthenticated()) { setSignInPrompt('Saving favorites'); return; }
                        toggleFavorite(site.id);
                      }}
                    >
                      {favorite ? 'Saved' : 'Favorite'}
                    </Button>
                    <Button
                      size="small"
                      color={wishlisted ? 'primary' : 'inherit'}
                      startIcon={wishlisted ? <BookmarkAdded /> : <BookmarkAdd />}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!authService.isAuthenticated()) { setSignInPrompt('Creating a dive wishlist'); return; }
                        toggleWishlist(site.id);
                      }}
                    >
                      {wishlisted ? 'Planned' : 'Want to dive'}
                    </Button>
                    <Button
                      size="small"
                      color={siteLists.length > 0 ? 'secondary' : 'inherit'}
                      startIcon={<CollectionsBookmark />}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!authService.isAuthenticated()) { setSignInPrompt('Managing dive site lists'); return; }
                        setListError(null);
                        setListDialogSite(site);
                      }}
                    >
                      {siteLists.length > 0 ? `Lists (${siteLists.length})` : 'Lists'}
                    </Button>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={isSubmitDialogOpen} onClose={() => setIsSubmitDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingSite ? 'Edit Your Shared Dive Site' : 'Add A Shared Dive Site'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {submitError ? <Alert severity="error">{submitError}</Alert> : null}
            <TextField label="Site name" value={formValues.name} onChange={(event) => setFormValues((current) => ({ ...current, name: event.target.value }))} fullWidth />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Region" value={formValues.region} onChange={(event) => setFormValues((current) => ({ ...current, region: event.target.value }))} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Country" value={formValues.country} onChange={(event) => setFormValues((current) => ({ ...current, country: event.target.value }))} fullWidth />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField label="Latitude" type="number" value={formValues.latitude} onChange={(event) => setFormValues((current) => ({ ...current, latitude: event.target.value }))} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Longitude" type="number" value={formValues.longitude} onChange={(event) => setFormValues((current) => ({ ...current, longitude: event.target.value }))} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Match radius (km)" type="number" value={formValues.matchRadiusKm} onChange={(event) => setFormValues((current) => ({ ...current, matchRadiusKm: event.target.value }))} fullWidth />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField select label="Site type" value={formValues.siteType} onChange={(event) => setFormValues((current) => ({ ...current, siteType: event.target.value }))} fullWidth>
                  {siteTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Difficulty" value={formValues.difficulty} onChange={(event) => setFormValues((current) => ({ ...current, difficulty: event.target.value }))} fullWidth>
                  <MenuItem value="Beginner">Beginner</MenuItem>
                  <MenuItem value="Intermediate">Intermediate</MenuItem>
                  <MenuItem value="Advanced">Advanced</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <TextField label="Summary" value={formValues.summary} onChange={(event) => setFormValues((current) => ({ ...current, summary: event.target.value }))} fullWidth multiline minRows={3} />
            <TextField label="Highlights" value={formValues.highlights} onChange={(event) => setFormValues((current) => ({ ...current, highlights: event.target.value }))} helperText="Comma-separated" fullWidth />
            <TextField label="Tags" value={formValues.tags} onChange={(event) => setFormValues((current) => ({ ...current, tags: event.target.value }))} helperText="Comma-separated" fullWidth />
            <TextField label="Aliases" value={formValues.aliases} onChange={(event) => setFormValues((current) => ({ ...current, aliases: event.target.value }))} helperText="Optional, comma-separated alternate names" fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => {
            setIsSubmitDialogOpen(false);
            resetForm();
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitCommunitySite} disabled={catalogSaving}>
            {catalogSaving ? (editingSite ? 'Saving...' : 'Adding...') : (editingSite ? 'Save changes' : 'Add dive site')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isCreateListDialogOpen} onClose={() => {
        setIsCreateListDialogOpen(false);
        resetListForm();
      }} fullWidth maxWidth="sm">
        <DialogTitle>Create A Dive-Site List</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {listError ? <Alert severity="error">{listError}</Alert> : null}
            <TextField label="List name" value={newListName} onChange={(event) => setNewListName(event.target.value)} fullWidth autoFocus />
            <TextField
              label="Description"
              value={newListDescription}
              onChange={(event) => setNewListDescription(event.target.value)}
              helperText="Optional. Use this for trip themes like shark week, training sites, or family-friendly destinations."
              fullWidth
              multiline
              minRows={3}
            />
            {userId ? (
              <TextField
                select
                label="Visibility"
                value={newListVisibility}
                onChange={(event) => setNewListVisibility(event.target.value as DiveSiteListVisibility)}
                helperText="Private lists stay personal. Link and invite-only modes are ready for shared-list workflows."
                fullWidth
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="link">Shared by link</MenuItem>
                <MenuItem value="invite-only">Invite only</MenuItem>
              </TextField>
            ) : (
              <Alert severity="info">Guest lists stay on this device until you sign in.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => {
            setIsCreateListDialogOpen(false);
            resetListForm();
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCustomList} disabled={preferencesSaving}>
            {preferencesSaving ? 'Creating...' : 'Create list'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(listDialogSite)} onClose={() => setListDialogSite(null)} fullWidth maxWidth="sm">
        <DialogTitle>Manage Site Lists</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {listError ? <Alert severity="error">{listError}</Alert> : null}
            {listDialogSite ? (
              <Typography variant="body2" color="text.secondary">
                Choose which named lists should include {listDialogSite.name}.
              </Typography>
            ) : null}

            {customLists.length === 0 ? (
              <Alert severity="info">Create your first custom dive-site list to start grouping sites by trip, region, training plan, or buddy itinerary.</Alert>
            ) : (
              customLists.map((list) => {
                const inList = listDialogSite ? isSiteInCustomList(list.listId, listDialogSite.id) : false;

                return (
                  <Paper key={list.listId} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {list.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {list.description || 'No description yet.'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" variant="outlined" label={`${list.siteIds.length} sites`} />
                        <Chip size="small" variant="outlined" icon={<Public fontSize="small" />} label={formatListVisibility(list.visibility)} />
                        <Button
                          size="small"
                          variant={inList ? 'contained' : 'outlined'}
                          onClick={async () => {
                            if (!listDialogSite) {
                              return;
                            }

                            try {
                              setListError(null);
                              await toggleSiteInCustomList(list.listId, listDialogSite.id);
                            } catch (err) {
                              setListError(err instanceof Error ? err.message : 'Failed to update dive-site list');
                            }
                          }}
                        >
                          {inList ? 'Remove' : 'Add'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button
            startIcon={<PlaylistAdd />}
            onClick={() => {
              setIsCreateListDialogOpen(true);
            }}
          >
            New list
          </Button>
          <Button onClick={() => setListDialogSite(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isAccessDialogOpen} onClose={() => setIsAccessDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Manage List Access</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {listError ? <Alert severity="error">{listError}</Alert> : null}
            <TextField label="List name" value={newListName} onChange={(event) => setNewListName(event.target.value)} fullWidth />
            <TextField
              label="Description"
              value={newListDescription}
              onChange={(event) => setNewListDescription(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              select
              label="Visibility"
              value={newListVisibility}
              onChange={(event) => setNewListVisibility(event.target.value as DiveSiteListVisibility)}
              helperText="Private stays owner-only. Link is public. Invite-only keeps a collaborator roster while remaining controlled."
              fullWidth
            >
              <MenuItem value="private">Private</MenuItem>
              <MenuItem value="link">Shared by link</MenuItem>
              <MenuItem value="invite-only">Invite only</MenuItem>
            </TextField>
            {newListVisibility === 'private' ? (
              <Alert severity="info">
                Private lists stay owner-only. Switch to invite-only if you want to add collaborators.
              </Alert>
            ) : (
              <>
                <TextField
                  label="Collaborator user IDs"
                  value={collaboratorUserIdsInput}
                  onChange={(event) => setCollaboratorUserIdsInput(event.target.value)}
                  helperText="Add collaborators from search below or paste one user ID per line as a fallback."
                  fullWidth
                  multiline
                  minRows={4}
                />
                <TextField
                  label="Find collaborators"
                  value={collaboratorSearch}
                  onChange={(event) => setCollaboratorSearch(event.target.value)}
                  helperText="Search by first name, last name, or email."
                  fullWidth
                />
              </>
            )}
            {newListVisibility !== 'private' && collaboratorUserIds.length > 0 ? (
              <Stack spacing={1}>
                {collaboratorUserIds.map((collaboratorUserId) => (
                  <Paper key={collaboratorUserId} variant="outlined" sx={{ p: 1.25 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {formatCollaboratorLabel(collaboratorUserId)}
                          </Typography>
                          {collaboratorUserId === userId ? <Chip size="small" color="primary" label="You" /> : null}
                        </Stack>
                        {collaboratorLookup.get(collaboratorUserId)?.email ? (
                          <Typography variant="body2" color="text.secondary">
                            {collaboratorLookup.get(collaboratorUserId)?.email}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary">
                          {collaboratorUserId}
                        </Typography>
                      </Box>
                      <Button size="small" color="error" onClick={() => removeCollaboratorUserId(collaboratorUserId)}>
                        Remove
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : null}
            {newListVisibility !== 'private' && collaboratorSearchLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Searching users...
                </Typography>
              </Stack>
            ) : null}
            {newListVisibility !== 'private' && !collaboratorSearchLoading && collaboratorSearch.trim().length >= 2 && collaboratorSearchResults.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No matching users found.
              </Typography>
            ) : null}
            {newListVisibility !== 'private' && !collaboratorSearchLoading && collaboratorSearchResults.length > 0 ? (
              <Stack spacing={1}>
                {collaboratorSearchResults.map((result) => {
                  const alreadyAdded = collaboratorUserIds.includes(result.userId);

                  return (
                    <Paper key={result.userId} variant="outlined" sx={{ p: 1.25 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {result.firstName} {result.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {result.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {result.userId}
                          </Typography>
                        </Box>
                        <Button size="small" variant={alreadyAdded ? 'outlined' : 'contained'} onClick={() => addCollaboratorUserId(result.userId)} disabled={alreadyAdded}>
                          {alreadyAdded ? 'Added' : 'Add collaborator'}
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            ) : null}
            {selectedCustomList?.shareCode && newListVisibility === 'link' ? (
              <Alert severity="info" icon={<ContentCopy fontSize="inherit" />}>
                Share URL: {`${window.location.origin}/dive-site-lists/shared/${selectedCustomList.shareCode}`}
              </Alert>
            ) : null}
            {selectedCustomList && newListVisibility !== 'link' ? (
              <Alert severity="info" icon={<ContentCopy fontSize="inherit" />}>
                Collaborator URL: {`${window.location.origin}/dive-site-lists/${selectedCustomList.listId}`}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          {newListVisibility === 'link' ? (
            <Button startIcon={<ContentCopy />} onClick={handleCopyShareLink} disabled={!selectedCustomList?.shareCode}>
              Copy share link
            </Button>
          ) : (
            <Button startIcon={<ContentCopy />} onClick={() => copyAccessLink(selectedCustomList ?? undefined)} disabled={!selectedCustomList?.listId}>
              Copy collaborator link
            </Button>
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setIsAccessDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveListAccess} disabled={preferencesSaving}>
              {preferencesSaving ? 'Saving...' : 'Save access'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <SignInPromptDialog
        open={signInPrompt !== null}
        onClose={() => setSignInPrompt(null)}
        action={signInPrompt ?? undefined}
      />
    </Box>
  );
};

export default DiveSites;